import { DependencyList, useEffect, useState } from "react";
import { requestApi } from "../shared/api";

interface ListResponse<T> {
  items: T[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface FoProduct {
  id: string;
  category: string;
  categoryId: string;
  name: string;
  price: string;
  salePrice: number;
  regularPrice: number;
  image: string;
  detailHtml?: string;
  images?: Array<{ id: string; url: string; imageRole: string }>;
}

export interface FoNotice {
  id: string;
  title: string;
  date: string;
  bodyHtml?: string;
}

export interface FoQuote {
  id: string;
  managementCode: string;
  productName: string;
  purpose: string;
  mood?: string;
  quantity?: number;
  budgetMin?: number;
  budgetMax?: number;
  status: string;
  desiredDeliveryDate?: string;
  createdAt: string;
  attachmentImageUrl?: string;
  latestOfferAmount?: number;
  offers?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface FoOrder {
  id: string;
  orderNumber: string;
  productName: string;
  quantity: number;
  totalAmount: number;
  status: string;
  orderedAt: string;
  [key: string]: unknown;
}

function mapOrder(item: Record<string, unknown>): FoOrder {
  return {
    ...item,
    id: String(item.id || ""),
    orderNumber: String(item.orderNumber || item.order_number || ""),
    productName: String(item.productName || item.product_name_snapshot || ""),
    quantity: Number(item.quantity || 0),
    totalAmount: Number(item.totalAmount ?? item.total_amount ?? 0),
    status: String(item.status || ""),
    orderedAt: String(item.orderedAt || item.ordered_at || ""),
  };
}

export interface FoInquiry {
  id: string;
  managementCode: string;
  inquiryType: string;
  title: string;
  status: string;
  createdAt: string;
  answerBody?: string;
  body?: string;
  [key: string]: unknown;
}

export interface CurrentUser {
  id: string;
  managementCode: string;
  loginId: string;
  email: string;
  name: string;
  phone?: string;
  gender?: string;
  birthDate?: string;
  postalCode?: string;
  addressLine1?: string;
  addressLine2?: string;
  avatarFileId?: string;
  avatarUrl?: string;
  authProviders?: string[];
}

function useApiResource<T>(loader: () => Promise<T>, initial: T, dependencies: DependencyList, options: { refreshOnFocus?: boolean; pollingMs?: number } = {}) {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    loader().then((result) => { if (active) setData(result); })
      .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : "데이터를 불러오지 못했습니다."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // The caller owns loader dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, reloadKey]);
  useEffect(() => {
    if (!options.refreshOnFocus && !options.pollingMs) return;
    const refresh = () => setReloadKey((value) => value + 1);
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    if (options.refreshOnFocus) {
      window.addEventListener("focus", refresh);
      document.addEventListener("visibilitychange", onVisibility);
    }
    const timer = options.pollingMs ? window.setInterval(refresh, options.pollingMs) : undefined;
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisibility);
      if (timer) window.clearInterval(timer);
    };
  }, [options.pollingMs, options.refreshOnFocus]);
  return { data, loading, error, reload: () => setReloadKey((value) => value + 1) };
}

function mapProduct(item: Record<string, unknown>): FoProduct {
  const images = Array.isArray(item.images) ? item.images as FoProduct["images"] : undefined;
  return {
    id: String(item.id), category: String(item.categoryName || ""), categoryId: String(item.categoryId || ""),
    name: String(item.name || ""), salePrice: Number(item.salePrice || 0), regularPrice: Number(item.regularPrice || 0),
    price: `${Number(item.salePrice || 0).toLocaleString("ko-KR")}원`, image: String(item.imageUrl || images?.[0]?.url || "/assets/product-1.png"),
    detailHtml: item.detailHtml ? String(item.detailHtml) : undefined, images,
  };
}

export function useProducts(options: { q?: string; category?: string; pageSize?: number } = {}) {
  const query = new URLSearchParams({ pageSize: String(options.pageSize || 100) });
  if (options.q) query.set("q", options.q);
  if (options.category && options.category !== "전체") query.set("category", options.category);
  const key = query.toString();
  return useApiResource(async () => {
    const result = await requestApi<ListResponse<Record<string, unknown>>>(`/products?${key}`);
    return result.items.map(mapProduct);
  }, [] as FoProduct[], [key]);
}

export function useProduct(id: string | null) {
  return useApiResource(async () => id ? mapProduct(await requestApi<Record<string, unknown>>(`/products/${id}`)) : null, null as FoProduct | null, [id]);
}

export function useNotices() {
  return useApiResource(async () => {
    const result = await requestApi<ListResponse<Record<string, unknown>>>("/notices?pageSize=100");
    return result.items.map((item): FoNotice => ({ id: String(item.id), title: String(item.title), date: String(item.publishStartAt || item.createdAt || "").slice(0, 10) }));
  }, [] as FoNotice[], []);
}

export function useNotice(id: string | null) {
  return useApiResource(async () => {
    if (!id) return null;
    const item = await requestApi<Record<string, unknown>>(`/notices/${id}`);
    return { id: String(item.id), title: String(item.title), date: String(item.publishStartAt || item.createdAt || "").slice(0, 10), bodyHtml: String(item.bodyHtml || "") } as FoNotice;
  }, null as FoNotice | null, [id]);
}

export function useQuotes() {
  return useApiResource(async () => (await requestApi<ListResponse<FoQuote>>("/quotes?pageSize=100")).items, [] as FoQuote[], [], { refreshOnFocus: true, pollingMs: 15000 });
}

export function useQuote(id: string | null) {
  return useApiResource(async () => {
    if (!id) return null;
    const item = await requestApi<Record<string, unknown>>(`/quotes/${id}`);
    return {
      ...item,
      id: String(item.id),
      managementCode: String(item.management_code || item.managementCode || ""),
      productName: String(item.product_name || item.productName || ""),
      purpose: String(item.purpose || ""),
      mood: item.mood ? String(item.mood) : undefined,
      quantity: item.quantity ? Number(item.quantity) : undefined,
      budgetMin: item.budget_min == null ? undefined : Number(item.budget_min),
      budgetMax: item.budget_max == null ? undefined : Number(item.budget_max),
      status: String(item.status || ""),
      desiredDeliveryDate: item.desired_delivery_date ? String(item.desired_delivery_date) : undefined,
      createdAt: String(item.created_at || ""),
      offers: Array.isArray(item.offers) ? item.offers as Array<Record<string, unknown>> : [],
    } as FoQuote;
  }, null as FoQuote | null, [id]);
}

export function useOrders() {
  return useApiResource(async () => {
    const result = await requestApi<ListResponse<Record<string, unknown>>>("/orders?pageSize=100");
    return result.items.map(mapOrder);
  }, [] as FoOrder[], []);
}

export function useOrder(id: string | null) {
  return useApiResource(async () => id ? mapOrder(await requestApi<Record<string, unknown>>(`/orders/${id}`)) : null, null as FoOrder | null, [id]);
}

export function useInquiries() {
  return useApiResource(async () => (await requestApi<ListResponse<FoInquiry>>("/inquiries?pageSize=100")).items, [] as FoInquiry[], []);
}

export function useInquiry(id: string | null) {
  return useApiResource(async () => id ? await requestApi<FoInquiry>(`/inquiries/${id}`) : null, null as FoInquiry | null, [id]);
}

export function useCurrentUser() {
  return useApiResource(() => requestApi<CurrentUser>("/auth/me"), null as CurrentUser | null, []);
}
